import { useState } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, ArrowRight, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const comparablePrograms = [
  {
    id: "pmkvy",
    name: "PMKVY",
    fullName: "Pradhan Mantri Kaushal Vikas Yojana",
    cost: "Free",
    duration: "3-6 months",
    stipend: false,
    stipendAmount: "—",
    mode: "Offline (Center-based)",
    certificate: true,
    placement: true,
    placementNote: "Assistance provided",
    eligibility: "10+2 Pass, Age 18-35",
    sectors: "40+ sectors",
    bestFor: "Quick job-ready skills",
    url: "https://www.skillindiadigital.gov.in/pmkvy-landing",
  },
  {
    id: "naps",
    name: "NAPS",
    fullName: "National Apprenticeship Promotion Scheme",
    cost: "Free",
    duration: "6-36 months",
    stipend: true,
    stipendAmount: "₹5,000 - ₹9,000/mo",
    mode: "Hybrid (Industry + Training)",
    certificate: true,
    placement: true,
    placementNote: "Often absorbed by employer",
    eligibility: "10+2 Pass, Age 14+",
    sectors: "All industries",
    bestFor: "Earn while learning",
    url: "https://www.apprenticeshipindia.gov.in",
  },
  {
    id: "skill-india",
    name: "Skill India Digital",
    fullName: "Skill India Digital Hub",
    cost: "Free",
    duration: "Varies",
    stipend: false,
    stipendAmount: "—",
    mode: "Online",
    certificate: true,
    placement: false,
    eligibility: "Anyone",
    sectors: "Multiple",
    bestFor: "Exploring all govt schemes",
    url: "https://skillindiadigital.gov.in",
  },
  {
    id: "swayam",
    name: "SWAYAM / NPTEL",
    fullName: "SWAYAM & NPTEL Free Courses",
    cost: "Free (Exam fee ₹1000 for cert)",
    duration: "4-16 weeks",
    stipend: false,
    stipendAmount: "—",
    mode: "Online",
    certificate: true,
    placement: false,
    eligibility: "Class 9+",
    sectors: "Academic + Professional",
    bestFor: "College-level learning free",
    url: "https://swayam.gov.in",
  },
];

const ComparePrograms = () => {
  const [selected, setSelected] = useState<string[]>(["pmkvy", "naps"]);

  const toggleProgram = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length > 1) setSelected(selected.filter((s) => s !== id));
    } else {
      if (selected.length < 3) setSelected([...selected, id]);
    }
  };

  const selectedPrograms = comparablePrograms.filter((p) => selected.includes(p.id));

  const rows: { label: string; key: string }[] = [
    { label: "Cost", key: "cost" },
    { label: "Duration", key: "duration" },
    { label: "Stipend/Income", key: "stipendAmount" },
    { label: "Mode", key: "mode" },
    { label: "Certificate", key: "certificate" },
    { label: "Placement Assistance", key: "placement" },
    { label: "Eligibility", key: "eligibility" },
    { label: "Sectors", key: "sectors" },
    { label: "Best For", key: "bestFor" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 bg-gradient-to-b from-accent/30 to-background">
        <div className="container py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Compare Programs
            </h1>
            <p className="text-muted-foreground mb-8">
              Side-by-side comparison to help you decide. Select 2-3 programs.
            </p>

            {/* Selector */}
            <div className="flex flex-wrap gap-2 mb-8">
              {comparablePrograms.map((p) => (
                <button
                  key={p.id}
                  onClick={() => toggleProgram(p.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                    selected.includes(p.id)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-foreground hover:border-primary/50"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>

            {/* Comparison Table */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="overflow-x-auto"
            >
              <Card className="border border-border">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="p-4 text-left text-sm font-semibold text-muted-foreground w-1/4">
                          Feature
                        </th>
                        {selectedPrograms.map((p) => (
                          <th key={p.id} className="p-4 text-center">
                            <div className="font-bold text-foreground">{p.name}</div>
                            <div className="text-xs text-muted-foreground mt-1">{p.fullName}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={row.key} className={i % 2 === 0 ? "bg-muted/30" : ""}>
                          <td className="p-4 text-sm font-medium text-foreground">{row.label}</td>
                          {selectedPrograms.map((p) => {
                            const val = (p as any)[row.key];
                            return (
                              <td key={p.id} className="p-4 text-center text-sm">
                                {typeof val === "boolean" ? (
                                  val ? (
                                    <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                                  ) : (
                                    <XCircle
                                      className="h-5 w-5 text-muted-foreground mx-auto"
                                      aria-label="Not available"
                                    />
                                  )
                                ) : (
                                  <span className="text-foreground">{val}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {/* Apply row */}
                      <tr className="border-t border-border">
                        <td className="p-4"></td>
                        {selectedPrograms.map((p) => (
                          <td key={p.id} className="p-4 text-center">
                            <Button asChild size="sm" variant="hero">
                              <a href={p.url} target="_blank" rel="noopener noreferrer">
                                Apply <ExternalLink className="h-3 w-3 ml-1" />
                              </a>
                            </Button>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </motion.div>

            <div className="text-center mt-8 space-x-4">
              <Button asChild variant="outline">
                <Link to="/career-quiz">
                  Take Career Quiz <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/programs">
                  Browse All Programs <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ComparePrograms;
